import { useInView } from "framer-motion";
import { sortBy } from "lodash";
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { disableComparator } from "../../../../features-config.json";
import { ProductsComparatorProvider } from "../../../contexts/products-comparator";
import { useDataLayer } from "../../../hooks/use-data-layer";
import { FilterContentGroup, formatText, GoogleAnalyticsEvent } from "../../../utils/analytics";
import {
  areFiltersEmpty,
  filterProducts,
  getDefaultProductFinderFilters,
  getFiltersFromSearchParams,
  setFiltersInSearchParams,
} from "../../../utils/products";
import { ProductComparatorCardProps } from "./product-comparator/product-comparator-card";
import {
  ProductComparatorPanel,
  ProductComparatorPanelProps,
} from "./product-comparator/product-comparator-panel";
import { ProductFinderHeader, ProductFinderHeaderProps } from "./product-finder-header";
import { ProductsGrid, ProductsGridProps } from "./products-grid";
import { ProductFinderFiltersFormTypes } from "./types";

export interface ProductFinderProps {
  filterSearchParamKey: string;
  products: Queries.ProductDataFragment[];
  specializations?: Queries.MachineSpecializationDataFragment[];
  specialization?: Queries.MachineSpecializationDataFragment;
  internalName?: string | null;
  labels: ProductComparatorCardProps["labels"] &
    ProductComparatorPanelProps["labels"] &
    ProductsGridProps["labels"] &
    ProductFinderHeaderProps["labels"];
}

export const ProductFinder: FC<ProductFinderProps> = ({
  products,
  labels,
  specializations: _specializations,
  specialization,
  internalName,
  filterSearchParamKey,
}) => {
  const { pushEvent } = useDataLayer();
  const [isFiltersPanelOpen, setFiltersPanelOpen] = useState(false);

  // If a specific specialization is passed, restrict available specializations to that
  const specializations = useMemo(() => {
    return specialization ? [specialization] : _specializations ?? [];
  }, [specialization, _specializations]);

  const [appliedFilters, setAppliedFilters] = useState<ProductFinderFiltersFormTypes>({
    ...getDefaultProductFinderFilters(specializations),
    ...getFiltersFromSearchParams(filterSearchParamKey),
  });

  const filtersForm = useForm<ProductFinderFiltersFormTypes>({
    defaultValues: appliedFilters,
  });

  const [filteredProducts, setFilteredProducts] = useState<Queries.ProductDataFragment[] | null>(
    null
  );

  // on submit form, save submitted filters in a dedicated state
  // this lets us reset the form to its previous submitted state if the user
  // closes the panel without submitting (so we avoid re-opening it in a dirty state)
  const onSubmit = useCallback(
    (values: ProductFinderFiltersFormTypes) => {
      setAppliedFilters(values);
      setFiltersPanelOpen(false);

      // Data Layer
      pushEvent({
        event: GoogleAnalyticsEvent.Filter,
        content_group: FilterContentGroup.ProductList,
      });
    },
    [pushEvent]
  );

  const { reset } = filtersForm;
  // Reset filters form on close panel
  useEffect(() => {
    if (!isFiltersPanelOpen) reset(appliedFilters);
    setFiltersInSearchParams(
      areFiltersEmpty(appliedFilters) ? null : appliedFilters,
      specializations,
      filterSearchParamKey
    );
  }, [isFiltersPanelOpen, appliedFilters, reset, specializations, filterSearchParamKey]);

  // Filter products by submitted filters
  useEffect(() => {
    const filteredProducts = filterProducts(
      products,
      appliedFilters,
      labels as Record<string, string>
    );
    const orderedProducts = sortBy(filteredProducts, (x) =>
      x.order === null ? Infinity : x.order
    );
    setFilteredProducts(orderedProducts);
  }, [products, appliedFilters, labels]);

  const gridRef = useRef<HTMLDivElement>(null);
  const isGridVisible = useInView(gridRef);
  const hasSentViewEvent = useRef(false);
  useEffect(() => {
    if (filteredProducts && isGridVisible && !hasSentViewEvent.current) {
      pushEvent(
        {
          event: GoogleAnalyticsEvent.ViewProductList,
          product_list_name: internalName,
          products: filteredProducts
            .filter((p) => p.defaultLocaleEntry)
            .map((product) => ({
              product_id: product.defaultLocaleEntry?.id ?? null,
              product_name: formatText(product.defaultLocaleEntry?.name ?? null),
              product_category: formatText(
                product.defaultLocaleEntry?.specialization?.name ?? null
              ),
              product_category2: formatText(
                product.defaultLocaleEntry?.specialization?.machiningType?.name ?? null
              ),
              product_category3: formatText(
                product.defaultLocaleEntry?.specialization?.machiningType?.material ?? null
              ),
            })),
        },
        ["products"]
      );
      hasSentViewEvent.current = true;
    }
  }, [isGridVisible, pushEvent, internalName, filteredProducts]);

  return (
    <ProductsComparatorProvider maxProducts={3}>
      <form onSubmit={filtersForm.handleSubmit(onSubmit)}>
        <FormProvider {...filtersForm}>
          <div
            ref={gridRef}
            className="p-8 lg:p-14 flex flex-col gap-4 bg-neutral-light rounded-t-3xl lg:rounded-t-4xl"
          >
            <ProductFinderHeader
              specializations={specializations}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              labels={labels}
              {...{ isFiltersPanelOpen, setFiltersPanelOpen }}
            />
            <ProductsGrid
              products={filteredProducts}
              isFiltered={areFiltersEmpty(appliedFilters)}
              labels={labels}
            />
          </div>
        </FormProvider>
      </form>
      {!disableComparator && <ProductComparatorPanel labels={labels} />}
    </ProductsComparatorProvider>
  );
};
